import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { useApi, useDebounced, useMutation } from '../lib/use-api';
import { EmptyNotice, ErrorNotice, Loading } from './Feedback';

const PAGE_SIZE = 20;

interface CandidateUser {
  principalId: string;
  email: string | null;
  displayName: string;
  status: string;
  roles: string[];
}

/**
 * Thêm người vào nhóm bằng cách chọn từ danh sách tài khoản đã có.
 *
 * Trước đây chiều vào nhóm chỉ một hướng — học viên tự nhập mã tham gia rồi
 * giáo viên duyệt — nên xếp lớp từ danh sách có sẵn phải sửa thẳng DB.
 *
 * Danh sách người dùng đến từ service `identity` (`identity: true`), còn lệnh
 * thêm đi tới delivery-api: tài khoản thuộc identity, quan hệ nhóm thuộc
 * delivery-api. Hai lời gọi hai nơi là đúng ranh giới sở hữu dữ liệu.
 */
export function AddMemberDialog({
  groupId,
  groupName,
  existingMemberIds,
  onClose,
  onAdded,
}: {
  groupId: string;
  groupName: string;
  /** Người đã ở trong nhóm — hiện mờ, không cho bấm thêm lần nữa. */
  existingMemberIds: ReadonlySet<string>;
  onClose: () => void;
  onAdded: () => void;
}): ReactNode {
  const { t } = useTranslation(['admin', 'common']);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [keyword, setKeyword] = useState('');
  const [addedIds, setAddedIds] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const debouncedKeyword = useDebounced(keyword);

  const users = useApi<{ items: CandidateUser[]; total: number }>('/api/admin/users', {
    query: { q: debouncedKeyword, limit: PAGE_SIZE, offset: 0 },
    identity: true,
  });

  const add = useMutation<string, { status: string }>(
    useCallback(
      (memberId: string) =>
        apiFetch<{ status: string }>(`/api/groups/${groupId}/members`, {
          method: 'POST',
          body: { memberId },
        }),
      [groupId],
    ),
  );

  // Đánh dấu tại chỗ thay vì đóng dialog sau mỗi lần thêm: xếp lớp là thao tác
  // lặp, đóng ra mở lại cho từng người là chỗ mất thời gian nhất của màn này.
  const submit = async (memberId: string): Promise<void> => {
    const result = await add.run(memberId);
    if (!result) return;
    setAddedIds((current) => new Set(current).add(memberId));
    onAdded();
  };

  const isInGroup = (user: CandidateUser): boolean =>
    existingMemberIds.has(user.principalId) || addedIds.has(user.principalId);

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <h2>{t('groups.addMember')}</h2>
      <p className="muted">{groupName}</p>

      <label className="field">
        <span>{t('groups.searchUsers')}</span>
        <input
          type="search"
          value={keyword}
          placeholder={t('groups.searchUsersPlaceholder')}
          onChange={(event) => setKeyword(event.target.value)}
        />
      </label>

      {add.error ? <ErrorNotice error={add.error} /> : null}
      {users.loading ? <Loading /> : null}
      {users.error ? <ErrorNotice error={users.error} onRetry={users.reload} /> : null}

      {users.data && !users.loading ? (
        users.data.items.length === 0 ? (
          <EmptyNotice message={t('groups.noUsersFound')} />
        ) : (
          <ul className="group-list">
            {users.data.items.map((user) => (
              <li key={user.principalId}>
                <strong>{user.displayName}</strong>
                {user.email ? <span className="muted"> {user.email}</span> : null}
                {isInGroup(user) ? (
                  <span className="chip chip--static">{t('groups.alreadyMember')}</span>
                ) : (
                  <button
                    type="button"
                    disabled={add.pending}
                    onClick={() => void submit(user.principalId)}
                  >
                    {t('groups.addToGroup')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )
      ) : null}

      <footer className="dialog__footer">
        <button type="button" onClick={() => dialogRef.current?.close()}>
          {t('common:action.close')}
        </button>
      </footer>
    </dialog>
  );
}
